import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import { slugToGenre, getGenreDescription, genreToSlug } from '@/lib/genreUtils';
import { useGenreBooks, useGenreAuthors } from '@/hooks/useGenreBooks';
import { BookCard } from '@/components/BookCard';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { ArrowLeft, BookOpen, Users, ChevronLeft, ChevronRight, TrendingUp, Star, Calendar } from 'lucide-react';
import { GENRE_OPTIONS, FICTION_GENRES, NONFICTION_GENRES } from '@/lib/constants';

const BOOKS_PER_PAGE = 24;

export default function GenrePage() {
  const { slug } = useParams<{ slug: string }>();
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState<'popularity' | 'rating' | 'recent'>('popularity');
  
  const genre = slug ? slugToGenre(slug) : null;
  const offset = (page - 1) * BOOKS_PER_PAGE;
  
  const { data, isLoading, error } = useGenreBooks({
    genre: genre || '',
    limit: BOOKS_PER_PAGE,
    offset,
    sortBy,
  });
  
  const { data: popularAuthors = [], isLoading: loadingAuthors } = useGenreAuthors(genre || '', 8);
  
  const totalPages = data ? Math.ceil(data.totalCount / BOOKS_PER_PAGE) : 0;

  // Get related genres (same category)
  const getRelatedGenres = () => {
    if (!genre) return [];
    const isFiction = (FICTION_GENRES as readonly string[]).includes(genre);
    const sameCategory = isFiction ? FICTION_GENRES : NONFICTION_GENRES;
    return (sameCategory as readonly string[])
      .filter(g => g !== genre)
      .slice(0, 6);
  };

  if (!genre) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-7xl w-full overflow-x-hidden">
        <Card>
          <CardContent className="p-8 text-center">
            <BookOpen className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h1 className="text-2xl font-bold mb-2">Genre not found</h1>
            <p className="text-muted-foreground mb-4">
              The genre you're looking for doesn't exist.
            </p>
            <Link to="/discover">
              <Button>Back to Discover</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl w-full overflow-x-hidden">
      {/* Back button */}
      <Link to="/discover">
        <Button variant="ghost" size="sm" className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Discover
        </Button>
      </Link>

      {/* Genre Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2">{genre}</h1>
        <p className="text-lg text-muted-foreground mb-4">
          {getGenreDescription(genre)}
        </p>
        {data && (
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <div className="flex items-center gap-1">
              <BookOpen className="h-4 w-4" />
              <span>{data.totalCount.toLocaleString()} books</span>
            </div>
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-4 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-3">
          {/* Sort Controls */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold">Books</h2>
            <Select value={sortBy} onValueChange={(v) => { setSortBy(v as typeof sortBy); setPage(1); }}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="popularity">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4" />
                    Most Popular
                  </div>
                </SelectItem>
                <SelectItem value="rating">
                  <div className="flex items-center gap-2">
                    <Star className="h-4 w-4" />
                    Highest Rated
                  </div>
                </SelectItem>
                <SelectItem value="recent">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Most Recent
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Books Grid */}
          {isLoading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {Array.from({ length: 12 }).map((_, i) => (
                <div key={i} className="space-y-2">
                  <Skeleton className="aspect-[2/3] w-full rounded-lg" />
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
              ))}
            </div>
          ) : error ? (
            <Card>
              <CardContent className="p-8 text-center">
                <p className="text-muted-foreground">Failed to load books. Please try again.</p>
              </CardContent>
            </Card>
          ) : data?.books.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <BookOpen className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-lg font-medium mb-2">No books found</p>
                <p className="text-muted-foreground">
                  We haven't indexed any books in this genre yet. Check back soon!
                </p>
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {data?.books.map((book) => (
                  <BookCard
                    key={book.id}
                    book={{
                      id: book.id,
                      title: book.title,
                      authors: book.authors || [],
                      coverUrl: book.cover_url,
                      coverCacheUrl: book.cover_cache_url,
                      isbn13: book.isbn13,
                      isbn10: book.isbn10,
                      publishedYear: book.published_year,
                    }}
                  />
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-8">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>
                  <span className="text-sm text-muted-foreground px-4">
                    Page {page} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Popular Authors */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Users className="h-5 w-5" />
                Popular Authors
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loadingAuthors ? (
                <div className="space-y-3">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Skeleton key={i} className="h-8 w-full" />
                  ))}
                </div>
              ) : popularAuthors.length === 0 ? (
                <p className="text-sm text-muted-foreground">No authors found</p>
              ) : (
                <div className="space-y-2">
                  {popularAuthors.map((author) => (
                    <Link
                      key={author.name}
                      to={`/author/${encodeURIComponent(author.name)}`}
                      className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <span className="font-medium text-sm truncate">{author.name}</span>
                      <Badge variant="secondary" className="text-xs">
                        {author.bookCount} {author.bookCount === 1 ? 'book' : 'books'}
                      </Badge>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Related Genres */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Related Genres</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {getRelatedGenres().map((relatedGenre) => (
                  <Link
                    key={relatedGenre}
                    to={`/genre/${genreToSlug(relatedGenre)}`}
                  >
                    <Badge variant="outline" className="cursor-pointer hover:bg-muted">
                      {relatedGenre}
                    </Badge>
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Browse All Genres */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Browse by Genre</CardTitle>
            </CardHeader>
            <CardContent className="max-h-64 overflow-y-auto">
              <div className="space-y-1">
                {GENRE_OPTIONS.map((g) => (
                  <Link
                    key={g}
                    to={`/genre/${genreToSlug(g)}`}
                    className={`block px-2 py-1.5 text-sm rounded hover:bg-muted/50 transition-colors ${
                      g === genre ? 'bg-primary/10 text-primary font-medium' : ''
                    }`}
                  >
                    {g}
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
